import { action, observable } from "mobx";
import IsdsResponse from "models/isdsResponse";
import EshopError from "../models/eshopError";
import EventBus, {
  customerRefundApplicationProduction,
  refundRequestCreationFailed,
  refundRequestDocumentsUploadFailed,
  refundRequestDocumentsUploadSuccess,
} from "./eventBus";
import { Notice } from "./NIA/niaService";
import { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "./omissionService";
import { PersistedBase64 } from "../models/notice";
import { plainToClass } from "class-transformer";
import * as Sentry from "@sentry/react";
import RefundRequestNotice from "../models/refundRequestNotice";
import RefundRequestRepository from "../repositories/refundRequestRepository";
import CustomerRefundApplicationProductionResponse from "entities/customerRefundApplicationProductionResponse";

export const POST_REFUND_REQUEST_CREATION_FAILED = "POST_REFUND_REQUEST_CREATION_FAILED";
export const POST_REFUND_REQUEST_DOCUMENTS_UPLOAD_FAILED = "POST_REFUND_REQUEST_DOCUMENTS_UPLOAD_FAILED";

export default class RefundRequestService {
  @observable notice: RefundRequestNotice = new RefundRequestNotice();
  @observable customerRefundApplicationProduction: CustomerRefundApplicationProductionResponse | undefined;

  constructor(private repository: RefundRequestRepository, private eventBus: EventBus) {
    this.initialize();
  }

  @action.bound
  async createRefundRequest(refundRequest: RefundRequestNotice, countryId: string, licensePlate: string) {
    return await this.repository
      .createRefundRequest(refundRequest, countryId, licensePlate)
      .then(async (response: any | EshopError) => {
        if (response instanceof EshopError) {
          this.eventBus.publish(refundRequestCreationFailed({ type: response.type }));
        } else if (response.blob && response.fileName) {
          return response;
        }
      })
      .catch(() => {
        this.eventBus.publish(refundRequestCreationFailed({ type: POST_REFUND_REQUEST_CREATION_FAILED }));
        return undefined;
      });
  }

  @action.bound
  async getProduction(refundRequest: RefundRequestNotice, countryId: string, licensePlate: string) {
    return await this.repository
      .getProduction(refundRequest, countryId, licensePlate)
      .then(response => {
        this.customerRefundApplicationProduction = response as CustomerRefundApplicationProductionResponse;
        return response;
      })
      .catch(() => this.eventBus.publish(customerRefundApplicationProduction({ customerRefundApplicationProduction: undefined })));
  }

  @action.bound
  async uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    sessionStorage.setItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY, Notice.REFUND_REQUEST);
    return await this.repository
      .uploadDocuments(ISDSSessionId, files)
      .then(response => {
        if (response instanceof IsdsResponse) {
          this.eventBus.publish(refundRequestDocumentsUploadSuccess({ conceptIdent: response.conceptIdent }));
        } else {
          this.publishDocumentsUploadFailure(response.type, response, files, ISDSSessionId);
        }
      })
      .catch(error => {
        this.publishDocumentsUploadFailure(POST_REFUND_REQUEST_DOCUMENTS_UPLOAD_FAILED, error, files, ISDSSessionId);
      });
  }

  private publishDocumentsUploadFailure(type: string, error: any, files: PersistedBase64[], sessionId: string) {
    Sentry.withScope(scope => {
      scope.setTag("RefundRequestService", "upload");
      scope.setExtras({
        type,
        files,
        sessionId,
      });
      Sentry.captureException(error);
    });

    this.eventBus.publish(refundRequestDocumentsUploadFailed({ type }));
  }

  @action.bound
  initialize() {
    const noticeString = sessionStorage.getItem(Notice.REFUND_REQUEST);
    this.notice = new RefundRequestNotice();

    if (noticeString) {
      this.notice = plainToClass(RefundRequestNotice, JSON.parse(noticeString));
    }
  }

  @action.bound
  reset = () => {
    this.notice = new RefundRequestNotice();
  };
}
